import { bucketIdentifiers } from "@dune/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { getPrivateFileUrl } from "../../../api/queries/files.queries";
import { useGetBrokerDetails as useGetSpecificBrokerDetails } from "../../../api/queries/societies.queries";
import { useGetBrokerDetails as useGetCurrentUserBrokerDetails } from "../../../api/queries/users.queries";
import { shouldShowGuarantee } from "./CompleteFiles";
import { RegisterSecondStepDetailsFormData } from "./UseRegisterSecondStepBroker";
import { toast } from "sonner";

const zFileSchema = z.object({
	size: z.number().refine((val) => val === -1 || val >= 1, {
		message: "Le fichier est requis",
	}),
	name: z.string().min(1, "Le nom est requis"),
	bucket: z.enum(bucketIdentifiers, {
		error: "Le bucket doit être 'private' ou 'public'",
	}),
	key: z.string().min(1, "La clé est requise"),
	type: z.string().min(1, "Le type est requis"),
	url: z.string().optional(),
});

// Type de fichier simple
export type FileData = z.infer<typeof zFileSchema>;

// Type plus simple pour éviter les problèmes de conversion avec garantie
export type RegisterSecondStepFilesFormData = {
	kbis: FileData;
	identity: FileData;
	rib: FileData;
	rc: FileData;
	guarantee?: FileData;
};

// Fonction simplifiée pour créer le schéma
export const createRegisterSecondStepFilesFormSchema = (
	isGuaranteeRequired: boolean,
) => {
	return z.object({
		kbis: zFileSchema,
		identity: zFileSchema,
		rib: zFileSchema,
		rc: zFileSchema,
		guarantee: isGuaranteeRequired ? zFileSchema : zFileSchema.optional(),
	});
};

const defaultFile: FileData = {
	size: 0,
	name: "",
	bucket: "private",
	key: "",
	type: "",
	url: "",
};

export function useRegisterFilesForSecondStepForm(
	categories?: RegisterSecondStepDetailsFormData["categories"],
	brokerId?: string,
) {
	const isGuaranteeRequired = shouldShowGuarantee(categories);
	const { data: currentUserBrokerData, isLoading: isLoadingCurrentUser } =
		useGetCurrentUserBrokerDetails();
	const { data: specificBrokerData, isLoading: isLoadingSpecificBroker } =
		useGetSpecificBrokerDetails(brokerId);

	const brokerDetails = brokerId ? specificBrokerData : currentUserBrokerData;
	const isBrokerDetailsLoading = brokerId
		? isLoadingSpecificBroker
		: isLoadingCurrentUser;
	const [isLoadingFiles, setIsLoadingFiles] = useState(false);

	// Création du schéma simple
	const schema = createRegisterSecondStepFilesFormSchema(isGuaranteeRequired);

	// Définition des valeurs par défaut
	const defaultValues: RegisterSecondStepFilesFormData = {
		kbis: { ...defaultFile },
		identity: { ...defaultFile },
		rib: { ...defaultFile },
		rc: { ...defaultFile },
		...(isGuaranteeRequired ? { guarantee: { ...defaultFile } } : {}),
	};

	const {
		control,
		handleSubmit,
		getValues,
		setValue,
		watch,
		reset,
		trigger,
		formState: { errors },
	} = useForm<RegisterSecondStepFilesFormData>({
		defaultValues,
		// biome-ignore lint/suspicious/noExplicitAny:
		resolver: zodResolver(schema) as any,
	});

	useEffect(() => {
		const loadFiles = async () => {
			if (brokerDetails?.files && brokerDetails.files.length > 0) {
				setIsLoadingFiles(true);

				try {
					for (const fileOnSociety of brokerDetails.files) {
						const { file, type } = fileOnSociety;

						const signedUrl = await getPrivateFileUrl(file.key);

						if (file.bucket !== "private" && file.bucket !== "public") {
							toast.error("Type de bucket de fichier inconnu");
							console.error(
								`Type de bucket inconnu pour le fichier: ${file.bucket}`,
							);
							continue;
						}
						const fileData = {
							size: -1,
							name: file.name,
							bucket: file.bucket,
							key: file.key,
							type: file.mimeType,
							url: signedUrl,
						} as const;

						switch (type) {
							case "KBIS":
								setValue("kbis", fileData);
								break;
							case "IDENTITY":
								setValue("identity", fileData);
								break;
							case "RIB":
								setValue("rib", fileData);
								break;
							case "RC":
								setValue("rc", fileData);
								break;
							case "GUARANTEE":
								if (isGuaranteeRequired) {
									setValue("guarantee", fileData);
								}
								break;
						}
					}
				} finally {
					setIsLoadingFiles(false);
				}
			} else {
				setIsLoadingFiles(false);
			}
		};

		loadFiles();
	}, [brokerDetails, isGuaranteeRequired, setValue]);

	return {
		control,
		handleSubmit,
		getValues,
		setValue,
		watch,
		reset,
		trigger,
		errors,
		isLoading: isBrokerDetailsLoading,
		isLoadingFiles,
	};
}
